import React, { useState } from 'react';
import { FileText, Download, Printer, Mail, Calendar } from 'lucide-react';
import { QueueingSystemParams, QueueingMetrics } from '../types';
import { QueueingCalculator } from '../utils/queueingCalculations';

interface ReportGeneratorProps {
  params: QueueingSystemParams;
  metrics: QueueingMetrics;
}

export const ReportGenerator: React.FC<ReportGeneratorProps> = ({ params, metrics }) => {
  const [reportType, setReportType] = useState<'executive' | 'technical' | 'operational'>('executive');

  const generateExecutiveReport = () => {
    const recommendations = [];
    
    if (metrics.utilization >= 0.95) {
      recommendations.push("Critical: System utilization is dangerously high. Immediate capacity expansion required.");
    } else if (metrics.utilization >= 0.85) {
      recommendations.push("Warning: System approaching capacity limits. Consider preventive measures.");
    }
    
    if (metrics.queueTime > metrics.processTime * 3) {
      recommendations.push("High queue times detected. Investigate process improvements or variability reduction.");
    }
    
    if (params.arrivalCV > 1.5 || params.serviceCV > 1.5) {
      recommendations.push("High system variability. Process standardization could yield significant improvements.");
    }

    return {
      title: "Executive Summary - Queueing System Analysis",
      sections: [
        {
          title: "Key Performance Indicators",
          content: [
            `System Utilization: ${QueueingCalculator.formatPercentage(metrics.utilization)}`,
            `Average Wait Time: ${QueueingCalculator.formatNumber(metrics.queueTime)} minutes`,
            `Total Processing Time: ${QueueingCalculator.formatNumber(metrics.cycleTime)} minutes`,
            `Work in Process: ${QueueingCalculator.formatNumber(metrics.wip)} items`,
            `System Efficiency: ${QueueingCalculator.formatPercentage(metrics.processTime / metrics.cycleTime)}`
          ]
        },
        {
          title: "System Status",
          content: [
            `Current Configuration: G/G/${params.servers} queueing system`,
            `Arrival Rate: ${params.arrivalRate} items/hour`,
            `Service Rate: ${params.serviceRate} items/hour per server`,
            `System Status: ${metrics.error ? 'Unstable' : 'Stable'}`
          ]
        },
        {
          title: "Strategic Recommendations",
          content: recommendations.length > 0 ? recommendations : ["System is operating within acceptable parameters."]
        }
      ]
    };
  };

  const generateTechnicalReport = () => {
    return {
      title: "Technical Analysis Report",
      sections: [
        {
          title: "Model Parameters",
          content: [
            `Arrival Process: λ = ${params.arrivalRate} items/hour, CV_a = ${params.arrivalCV}`,
            `Service Process: μ = ${params.serviceRate} items/hour, CV_p = ${params.serviceCV}`,
            `Server Configuration: m = ${params.servers}`,
            `Traffic Intensity: ρ = ${QueueingCalculator.formatNumber(params.arrivalRate / params.serviceRate)}`,
            `Variability Factor: (CV_a² + CV_p²)/2 = ${QueueingCalculator.formatNumber((Math.pow(params.arrivalCV, 2) + Math.pow(params.serviceCV, 2)) / 2)}`
          ]
        },
        {
          title: "VUT Equation Results",
          content: [
            `Utilization: u = λ/(m×μ) = ${QueueingCalculator.formatPercentage(metrics.utilization)}`,
            `Process Time: t_p = 1/μ = ${QueueingCalculator.formatNumber(metrics.processTime)} minutes`,
            `Queue Time: t_q = ${QueueingCalculator.formatNumber(metrics.queueTime)} minutes`,
            `Cycle Time: CT = t_q + t_p = ${QueueingCalculator.formatNumber(metrics.cycleTime)} minutes`,
            `Little's Law Verification: WIP = λ × CT = ${QueueingCalculator.formatNumber(metrics.wip)} items`
          ]
        },
        {
          title: "Mathematical Formulation",
          content: [
            params.servers === 1 ? 
              "Single Server: t_q = ((CV_a² + CV_p²)/2) × (u/(1-u)) × t_p" :
              `Multi-Server: t_q = ((CV_a² + CV_p²)/2) × (u^(√(2(m+1))-1))/(m(1-u)) × t_p`,
            `Where: u = ${QueueingCalculator.formatNumber(metrics.utilization)}, m = ${params.servers}`,
            `Variability Term = ${QueueingCalculator.formatNumber((Math.pow(params.arrivalCV, 2) + Math.pow(params.serviceCV, 2)) / 2)}`
          ]
        }
      ]
    };
  };

  const generateOperationalReport = () => {
    const poolingAnalysis = params.servers > 1 ? QueueingCalculator.calculatePoolingComparison(params) : null;
    
    return {
      title: "Operational Performance Report",
      sections: [
        {
          title: "Current Performance",
          content: [
            `Daily Throughput: ${QueueingCalculator.formatNumber(metrics.throughput * 60 * 24)} items/day`,
            `Average Queue Length: ${QueueingCalculator.formatNumber(metrics.wipQueue)} items`,
            `Server Utilization: ${QueueingCalculator.formatPercentage(metrics.utilization)} per server`,
            `Service Level: ${metrics.queueTime <= metrics.processTime ? 'Excellent' : metrics.queueTime <= metrics.processTime * 2 ? 'Good' : 'Poor'}`,
            `Capacity Headroom: ${QueueingCalculator.formatPercentage(1 - metrics.utilization)}`
          ]
        },
        {
          title: "Capacity Analysis",
          content: [
            `Maximum Safe Arrival Rate: ${QueueingCalculator.formatNumber(params.serviceRate * params.servers * 0.85)} items/hour`,
            `Current vs. Maximum: ${QueueingCalculator.formatPercentage(params.arrivalRate / (params.serviceRate * params.servers * 0.85))}`,
            `Recommended Operating Level: 85% utilization`,
            `Demand Growth Buffer: ${QueueingCalculator.formatNumber((params.serviceRate * params.servers * 0.85 - params.arrivalRate) / params.arrivalRate * 100)}%`
          ]
        },
        ...(poolingAnalysis ? [{
          title: "Pooling vs. Dedicated Analysis",
          content: [
            `Dedicated Queue Time: ${QueueingCalculator.formatNumber(poolingAnalysis.dedicated.queueTime)} minutes`,
            `Pooled Queue Time: ${QueueingCalculator.formatNumber(poolingAnalysis.pooled.queueTime)} minutes`,
            `Improvement Factor: ${QueueingCalculator.formatNumber(poolingAnalysis.dedicated.queueTime / poolingAnalysis.pooled.queueTime)}x`,
            `WIP Reduction: ${QueueingCalculator.formatNumber(poolingAnalysis.dedicated.totalWIP - poolingAnalysis.pooled.wip)} items`
          ]
        }] : [])
      ]
    };
  };

  const getCurrentReport = () => {
    switch (reportType) {
      case 'executive': return generateExecutiveReport();
      case 'technical': return generateTechnicalReport();
      case 'operational': return generateOperationalReport();
      default: return generateExecutiveReport();
    }
  };

  const exportReport = (format: 'pdf' | 'json' | 'csv') => {
    const report = getCurrentReport();
    const timestamp = new Date().toISOString();
    
    if (format === 'json') {
      const exportData = {
        timestamp,
        reportType,
        parameters: params,
        metrics,
        report
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `queueing-report-${reportType}-${timestamp.split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (format === 'csv') {
      const csvData = [
        ['Metric', 'Value', 'Unit'],
        ['Utilization', metrics.utilization.toString(), '%'],
        ['Process Time', metrics.processTime.toString(), 'minutes'],
        ['Queue Time', metrics.queueTime.toString(), 'minutes'],
        ['Cycle Time', metrics.cycleTime.toString(), 'minutes'],
        ['WIP', metrics.wip.toString(), 'items'],
        ['Queue WIP', metrics.wipQueue.toString(), 'items'],
        ['Throughput', (metrics.throughput * 60).toString(), 'items/hour']
      ];
      
      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `queueing-metrics-${timestamp.split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const report = getCurrentReport();

  return (
    <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-200 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Analysis Report
        </h3>
        <div className="flex items-center space-x-2">
          <select 
            value={reportType}
            onChange={(e) => setReportType(e.target.value as any)}
            className="bg-dark-700 border border-dark-600 rounded px-3 py-1 text-sm text-gray-200"
          >
            <option value="executive">Executive Summary</option>
            <option value="technical">Technical Analysis</option>
            <option value="operational">Operational Report</option>
          </select>
        </div>
      </div>

      <div className="space-y-6 mb-6">
        <h4 className="text-xl font-bold text-gray-100">{report.title}</h4>
        
        {report.sections.map((section, index) => (
          <div key={index} className="space-y-3">
            <h5 className="text-lg font-semibold text-primary-400">{section.title}</h5>
            <div className="space-y-2">
              {section.content.map((item, itemIndex) => (
                <p key={itemIndex} className="text-sm text-gray-300 pl-4 border-l-2 border-dark-600">
                  {item}
                </p>
              ))}
            </div>
          </div>
        ))}
        
        <div className="text-xs text-gray-500 pt-4 border-t border-dark-600">
          Generated on {new Date().toLocaleString()} | System: G/G/{params.servers} Queueing Model
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => exportReport('json')}
          className="btn-secondary text-xs px-3 py-2 flex items-center space-x-1"
        >
          <Download className="w-3 h-3" />
          <span>JSON</span>
        </button>
        <button
          onClick={() => exportReport('csv')}
          className="btn-secondary text-xs px-3 py-2 flex items-center space-x-1"
        >
          <Download className="w-3 h-3" />
          <span>CSV</span>
        </button>
        <button
          onClick={() => window.print()}
          className="btn-secondary text-xs px-3 py-2 flex items-center space-x-1"
        >
          <Printer className="w-3 h-3" />
          <span>Print</span>
        </button>
      </div>
    </div>
  );
};